import { useEffect, useRef, useCallback, useState } from 'react';

// Issue #1439: StrictMode double-mount guard.
// React 18 StrictMode fires setup -> cleanup -> setup within a few ms in
// dev. Two `new EventSource(...)` calls that close-then-reopen inside the
// backend's 10-second rate-limit window count as 2 requests — with 3 tabs
// that's enough to trip the 6/10s limiter. A module-scoped timestamp of
// the last teardown lets a fresh mount delay construction by ~100ms,
// absorbing the StrictMode cycle without blocking legitimate reconnects.
let _lastClosedAt = 0;
const STRICTMODE_GUARD_MS = 100;

// Issue #1439: Frontend reconnect policy split.
// - `onerror` with NO prior `onopen` -> likely server rejection (429/auth).
//   EventSource can't surface the status code (WHATWG spec), so we assume
//   the worst and wait the full limiter window + jitter before retrying.
// - `onerror` AFTER `onopen` -> network hiccup / server disconnect. Fast
//   retry (1s) keeps UX snappy.
const REJECT_COOLDOWN_MS = 10000; // matches server Retry-After header
const REJECT_JITTER = 0.2; // +/- 20%
const DISCONNECT_RETRY_MS = 1000;
// A rejection with no successful open can be a transient 429 (worth retrying)
// OR a dead auth — expired session cookie / logged-out tab left open. Since
// EventSource can't read the 401, retrying every 10s forever turns an
// abandoned tab into a permanent /api/users/events request storm (prod: this
// is ~all of the endpoint's 4xx). Back off exponentially from the 10s
// Retry-After up to a ceiling so a dead tab decays to ~1 retry / 5min instead,
// while a real 429 still recovers on its first-few retries and a recovered
// connection resets the streak on `onopen`.
const REJECT_MAX_MS = 300000; // 5 min ceiling for a dead/abandoned tab

// Pure backoff schedule (pre-jitter) for consecutive rejections. Exported for
// unit testing. streak 0 -> 10s, 1 -> 20s, ... capped at REJECT_MAX_MS.
export function rejectBackoffBaseMs(streak) {
  return Math.min(REJECT_COOLDOWN_MS * 2 ** streak, REJECT_MAX_MS);
}

/**
 * Global SSE connection — always active.
 * Receives: session_update, memory_update, heartbeat
 * Replaces useRealtimeEvents for global (non-session) events.
 *
 * @param {string} viUserId - Used only to determine whether the user is authenticated
 * @param {{ onSessionUpdate?: function, onMemoryUpdate?: function }} callbacks
 * @returns {{ connected: boolean }}
 */
export function useGlobalEventStream(viUserId, { onSessionUpdate, onMemoryUpdate } = {}) {
  const [connected, setConnected] = useState(false);
  const [reconnectTrigger, setReconnectTrigger] = useState(0);
  const eventSourceRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const openedRef = useRef(false); // true once the current EventSource saw `onopen`
  const rejectStreakRef = useRef(0); // consecutive rejections with no `onopen`
  // Stable refs for callbacks to avoid effect re-runs
  const callbacksRef = useRef({ onSessionUpdate, onMemoryUpdate });
  useEffect(() => { callbacksRef.current = { onSessionUpdate, onMemoryUpdate }; }, [onSessionUpdate, onMemoryUpdate]);

  // Close existing EventSource
  const closeEventSource = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      _lastClosedAt = Date.now();
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    openedRef.current = false;
    queueMicrotask(() => setConnected(false));
  }, []);

  useEffect(() => {
    if (!viUserId) {
      closeEventSource();
      return;
    }

    const apiUrl = import.meta.env.VITE_API_URL || '';
    const url = `${apiUrl}/api/users/events`;

    // StrictMode guard — delay construction if another EventSource just
    // tore down. `openStartDelay=0` on the steady-state path.
    const sinceClose = Date.now() - _lastClosedAt;
    const openStartDelay = sinceClose < STRICTMODE_GUARD_MS
      ? STRICTMODE_GUARD_MS - sinceClose
      : 0;

    let es = null;
    let cancelled = false;
    const startTimer = setTimeout(() => {
      if (cancelled) return;
      es = new EventSource(url, { withCredentials: true });
      eventSourceRef.current = es;

      es.onopen = () => {
        openedRef.current = true;
        rejectStreakRef.current = 0; // a successful open clears the reject backoff
        setConnected(true);
        console.log('[SSE:global] connected');
      };

      es.addEventListener('session_update', (e) => {
        console.log('[SSE:global] ← session_update');
        try { callbacksRef.current.onSessionUpdate?.(JSON.parse(e.data)); } catch { /* parse error — skip */ }
      });

      es.addEventListener('memory_update', (e) => {
        console.log('[SSE:global] ← memory_update');
        try { callbacksRef.current.onMemoryUpdate?.(JSON.parse(e.data)); } catch { /* parse error — skip */ }
      });

      // Heartbeat — confirms connection is alive, no action needed
      es.addEventListener('heartbeat', () => {
        console.log('[SSE:global] ♥');
      });

      es.onerror = () => {
        // EventSource never exposes status codes. Distinguish "server
        // rejected us" from "connection dropped mid-stream" by whether
        // `onopen` fired on this attempt.
        const wasOpen = openedRef.current;
        console.log('[SSE:global] error/reconnecting (wasOpen=' + wasOpen + ')');
        es.close();
        eventSourceRef.current = null;
        _lastClosedAt = Date.now();
        openedRef.current = false;
        setConnected(false);

        let delay;
        if (wasOpen) {
          delay = DISCONNECT_RETRY_MS;
        } else {
          // Exponential backoff over consecutive rejections (10s -> ... -> 5min
          // cap), with +/-20% jitter to desync multiple tabs rejected together.
          // First retry stays at the server Retry-After (10s) so a real 429
          // recovers fast; a persistently-rejected (dead-auth) tab decays.
          const base = rejectBackoffBaseMs(rejectStreakRef.current);
          rejectStreakRef.current += 1;
          const j = 1 - REJECT_JITTER + Math.random() * (REJECT_JITTER * 2);
          delay = Math.round(base * j);
        }
        reconnectTimerRef.current = setTimeout(() => {
          setReconnectTrigger((prev) => prev + 1);
        }, delay);
      };
    }, openStartDelay);

    return () => {
      cancelled = true;
      clearTimeout(startTimer);
      if (es) {
        es.close();
        eventSourceRef.current = null;
        _lastClosedAt = Date.now();
      }
      openedRef.current = false;
      setConnected(false);
    };
  }, [viUserId, closeEventSource, reconnectTrigger]);

  // Cleanup on unmount
  useEffect(() => {
    return () => closeEventSource();
  }, [closeEventSource]);

  return { connected };
}
